import React from 'react';
import { Box, Typography } from '@mui/material';
import { getSefirahName, getOmerDayHebrew, WEEK_NUMBER_LABEL } from '../../omerUtils';

// ── תפילה אחרי ספירה — WeekView ──────────────────────────────────────────────
const AfterCountSection = ({ section }) => (
  <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid #e2e8f0', width: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, #c8a84b)' }} />
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.58rem',
        color: '#8b6914',
        whiteSpace: 'nowrap',
      }}>
        ✦ {section.title} ✦
      </Typography>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, #c8a84b, transparent)' }} />
    </Box>
    {section.lines.map((line) => (
      <Typography key={line.id} sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.62rem',
        color: '#475569',
        lineHeight: 1.85,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {line.text}
      </Typography>
    ))}
  </Box>
);

const SEFIROT_SHORT = ['חֶסֶד', 'גְּבוּרָה', 'תִּפְאֶרֶת', 'נֶצַח', 'הוֹד', 'יְסוֹד', 'מַלְכוּת'];

// ── תבנית תצוגת שבוע ─────────────────────────────────────────────────────────
const WeekView = ({
  omerDay, isOmerSeason,
  isPreview, previewCountdown,
  mainCountText, subCountText, weekNumber, dayInWeek,
  showBracha, brachaText,
  showLeshemYichud, leshemYichudLines,
  showPasuk, pasukText,
  showAfterCount, afterCountSections,
  offSeasonMessage,
  title, showTitle,
  bgColor,
}) => {
  const bg = bgColor || '#ffffff';

  if (!isOmerSeason) {
    return (
      <Box sx={{
        background: bg,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'rtl',
      }}>
        <Typography sx={{ color: '#94a3b8', fontFamily: 'Frank Ruhl Libre, serif', fontSize: '0.9rem' }}>
          {offSeasonMessage}
        </Typography>
      </Box>
    );
  }

  // ימי השבוע הנוכחי: מיום ראשון של השבוע עד יום שביעי
  const weekStart = (weekNumber - 1) * 7 + 1;

  // נקודות שבועות (1–7)
  const weekDots = Array.from({ length: 7 }, (_, i) => i + 1);

  return (
    <Box sx={{
      background: bg,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '12px 14px',
      direction: 'rtl',
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: 2 },
    }}>
      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: '0.65rem',
          color: '#94a3b8',
          letterSpacing: '0.12em',
          mb: 0.75,
        }}>
          {title}
        </Typography>
      )}

      {/* באנר ספירה לאחור */}
      {isPreview && previewCountdown && (
        <Box sx={{
          mb: 0.75, px: 1.5, py: 0.5,
          background: '#f0f4ff',
          border: '1px solid #c7d2fe',
          borderRadius: 1,
          width: '100%',
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.68rem',
            color: '#1e3a5f',
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {previewCountdown}
          </Typography>
        </Box>
      )}

      {/* נקודות שבועות */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1, alignItems: 'center' }}>
        {weekDots.map((w) => (
          <Box key={w} sx={{
            width: w === weekNumber ? 10 : 7,
            height: w === weekNumber ? 10 : 7,
            borderRadius: '50%',
            background: w < weekNumber
              ? '#1e3a5f'
              : w === weekNumber
                ? '#e6c45e'
                : '#e2e8f0',
            transition: 'all 0.2s',
            border: w === weekNumber ? '1px solid #c9a84c' : 'none',
          }} />
        ))}
      </Box>

      {/* כותרת שבוע */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.65rem',
        color: '#8b6914',
        mb: 1,
        letterSpacing: '0.06em',
      }}>
        {WEEK_NUMBER_LABEL[weekNumber]}
      </Typography>

      {/* לשם ייחוד */}
      {showLeshemYichud && (
        <Box sx={{
          mb: 1.25, px: 1.5, py: 0.75,
          background: '#fafafa',
          borderRadius: 1,
          border: '1px solid #e2e8f0',
          width: '100%',
        }}>
          {leshemYichudLines.map((line, i) => (
            <Typography key={i} sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              fontSize: '0.57rem',
              color: '#64748b',
              lineHeight: 1.9,
              textAlign: 'center',
              direction: 'rtl',
            }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}

      {/* פסוק */}
      {showPasuk !== 'none' && pasukText && (
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.6rem',
            color: '#64748b',
            lineHeight: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            direction: 'rtl',
          }}>
            {pasukText}
          </Typography>
        </Box>
      )}

      {/* ברכה */}
      {showBracha && (
        <Box sx={{
          mb: 1.25, px: 1.5, py: 0.75,
          background: '#f8fafc',
          borderRadius: 1,
          border: '1px solid #e2e8f0',
          width: '100%',
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.68rem',
            color: '#1e293b',
            lineHeight: 2,
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {brachaText}
          </Typography>
        </Box>
      )}

      {/* 7 תיבות ימי השבוע */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', width: '100%', mb: 1 }}>
        {Array.from({ length: 7 }, (_, i) => {
          const day         = weekStart + i;
          const isToday     = day === omerDay;
          const isPast      = day < omerDay;
          const isFuture    = day > omerDay;
          const withinRange = day >= 1 && day <= 49;
          const sefirah     = withinRange ? SEFIROT_SHORT[i] : '';

          return (
            <Box key={i} sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              py: 0.75,
              px: 0.25,
              borderRadius: '8px',
              border: isToday
                ? '2px solid #e6c45e'
                : isPast
                  ? '1px solid #cbd5e1'
                  : '1px solid #e2e8f0',
              background: isToday
                ? '#0f1e35'
                : isPast
                  ? '#f1f5f9'
                  : '#fafafa',
              opacity: !withinRange ? 0.25 : isFuture ? 0.55 : 1,
              boxShadow: isToday ? '0 2px 8px rgba(230,196,94,0.2)' : 'none',
            }}>
              <Typography sx={{
                fontFamily: 'Frank Ruhl Libre, serif',
                fontSize: 'clamp(0.55rem, 1.8vw, 0.75rem)',
                color: isToday ? '#e6c45e' : isPast ? '#1e3a5f' : '#94a3b8',
                fontWeight: isToday ? 700 : 400,
                lineHeight: 1,
              }}>
                {withinRange ? getOmerDayHebrew(day) : ''}
              </Typography>
              <Typography sx={{
                fontFamily: 'Frank Ruhl Libre, serif',
                fontSize: 'clamp(0.33rem, 1vw, 0.46rem)',
                color: isToday ? 'rgba(230,196,94,0.7)' : '#94a3b8',
                lineHeight: 1,
                textAlign: 'center',
                maxWidth: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
                {sefirah}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* שורת ספירה */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(0.8rem, 2.5vw, 1.05rem)',
        color: '#1e3a5f',
        fontWeight: 600,
        textAlign: 'center',
        direction: 'rtl',
        mb: 0.25,
      }}>
        {mainCountText}
      </Typography>

      {subCountText && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(0.65rem, 1.8vw, 0.85rem)',
          color: '#64748b',
          textAlign: 'center',
          direction: 'rtl',
        }}>
          {subCountText}
        </Typography>
      )}

      {/* תפילות אחרי ספירה */}
      {showAfterCount && afterCountSections.length > 0 && (
        <Box sx={{ width: '100%' }}>
          {afterCountSections.map((section) => (
            <AfterCountSection key={section.id} section={section} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WeekView;
